import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table";
import { FlaskConical, ShieldCheck, Pill, Link as LinkIcon, Layers } from "lucide-react";
import { products, categoryKeys } from "../data/products";
import { useScrollReveal } from "../hooks/useAnimations";

const iconMap = { antibiotic: Pill, coccidiostat: ShieldCheck, api: FlaskConical, binder: LinkIcon, otherPremix: Layers };

function ProductDetailsContent({ details, isEn }) {
  return (
    <div className="space-y-4 text-sm text-slate-700">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
          {isEn ? "Overview" : "产品概述"}
        </p>
        <p className="leading-relaxed">
          {isEn ? details.summary : details.summaryZh}
        </p>
      </div>

      {(details.compositionTable || details.composition) && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-2">
            {details.compositionTable
              ? isEn ? "Guaranteed Nutritional Composition" : "保证营养组成"
              : isEn ? "Composition" : "成分"}
          </p>
          {details.compositionTable ? (
            <Table className="border border-slate-300 text-sm">
              <TableHeader>
                <TableRow className="border-b border-slate-300 hover:bg-transparent">
                  <TableHead className="border-r border-slate-300 text-center font-bold text-slate-800 h-10">
                    {isEn ? "No" : "序号"}
                  </TableHead>
                  <TableHead className="border-r border-slate-300 text-center font-bold text-slate-800">
                    {isEn ? "Nutritional Ingredients" : "营养成分"}
                  </TableHead>
                  <TableHead className="text-center font-bold text-slate-800">
                    {isEn ? "Guaranteed Nutritional Composition" : "保证营养组成"}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(isEn ? details.compositionTable : details.compositionTableZh).map((row) => (
                  <TableRow key={row.no} className="border-b border-slate-300 hover:bg-transparent">
                    <TableCell className="border-r border-slate-300 text-center font-medium">{row.no}</TableCell>
                    <TableCell className="border-r border-slate-300 text-center">{row.ingredient}</TableCell>
                    <TableCell className="text-center font-medium">{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <ul className="space-y-1 list-disc pl-4">
              {(isEn ? details.composition : details.compositionZh).map((item, idx) => (
                <li key={`composition-${idx}`} className="leading-relaxed">
                  <strong>{item.split(":")[0]}:</strong>
                  {item.includes(":") ? ` ${item.split(":").slice(1).join(":").trim()}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {details.features && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
            {isEn ? "Product Features" : "产品特点"}
          </p>
          <ul className="space-y-2 list-disc pl-4">
            {(isEn ? details.features : details.featuresZh).map((item, idx) => (
              <li key={`features-${idx}`} className="leading-relaxed">{item}</li>
            ))}
          </ul>
        </div>
      )}

      {(details.benefits || details.uses) && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
            {details.benefits
              ? isEn ? "Product Benefits" : "产品功效"
              : isEn ? "Uses" : "用途"}
          </p>
          <ul className="space-y-1 list-disc pl-4">
            {(isEn
              ? details.benefits || details.uses
              : details.benefitsZh || details.usesZh
            ).map((item, idx) => (
              <li key={`benefits-${idx}`} className="leading-relaxed">{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
          {isEn ? "Dosage & Administration" : "用量与使用方法"}
        </p>
        <p className="leading-relaxed">
          {isEn ? details.dosage : details.dosageZh}
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
            {isEn ? "Shelf Life" : "保质期"}
          </p>
          <p className="font-medium">{isEn ? details.shelfLife : details.shelfLifeZh}</p>
        </div>
        {details.countryOfOrigin && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
              {isEn ? "Country of Origin" : "原产地"}
            </p>
            <p className="font-medium">{isEn ? details.countryOfOrigin : details.countryOfOriginZh}</p>
          </div>
        )}
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
            {isEn ? "Packaging" : "包装"}
          </p>
          <p className="font-medium">{isEn ? details.packaging : details.packagingZh}</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
            {isEn ? "Precaution" : "注意事项"}
          </p>
          <p className="leading-relaxed font-medium">
            {isEn ? details.precaution : details.precautionZh}
          </p>
        </div>
      </div>

      {details.exporter && (
        <div className="rounded-lg border border-brand-blue/20 bg-card-mint/50 p-3">
          <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
            {isEn ? (details.exporter.title || "Exported By") : (details.exporterZh.title || "出口方")}
          </p>
          {details.exporter.name && (
            <>
              <p className="font-bold text-[#333333]">{isEn ? details.exporter.name : details.exporterZh.name}</p>
              <p className="leading-relaxed">{isEn ? details.exporter.address : details.exporterZh.address}</p>
            </>
          )}
          <p>
            <strong>{isEn ? "E-Mail:" : "邮箱："}</strong>{" "}
            {isEn ? details.exporter.email : details.exporterZh.email}
          </p>
          <p>
            <strong>{isEn ? "Cell:" : "电话："}</strong>{" "}
            {isEn ? details.exporter.phone : details.exporterZh.phone}
          </p>
          <p>
            <strong>{isEn ? "Website:" : "网站："}</strong>{" "}
            <a
              href={isEn ? details.exporter.website : details.exporterZh.website}
              target="_blank"
              rel="noreferrer"
              className="text-brand-blue font-semibold hover:underline"
            >
              {isEn ? details.exporter.website : details.exporterZh.website}
            </a>{" "}
            <span className="text-slate-500">
              {isEn ? details.exporter.websiteNote : details.exporterZh.websiteNote}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

export default function ProductCatalogue({ t, lang }) {
  const [activeCategory, setActiveCategory] = useState("antibiotic");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [headerRef, headerVisible] = useScrollReveal();
  const [contentRef, contentVisible] = useScrollReveal({ threshold: 0.05 });
  const isEn = lang === "en";
  const activeProducts = products[activeCategory];
  const isSingleProductCategory = activeProducts.length === 1;

  const Icon = iconMap[activeCategory];

  return (
    <section id="products" data-testid="products-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`reveal ${headerVisible ? 'visible' : ''}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[2px] bg-brand-blue" />
            <span className="text-brand-blue text-xs font-semibold tracking-[0.25em] uppercase">
              {t.products.sectionLabel}
            </span>
          </div>
          <h2 data-testid="products-title" className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.2rem] text-brand-blue mb-3 tracking-tight">
            {t.products.title}
          </h2>
          <p className="text-slate-600 max-w-xl mb-8 text-base">{t.products.subtitle}</p>

          <div className="flex flex-wrap gap-2 mb-10">
            {categoryKeys.map((key) => (
              <button
                key={key}
                data-testid={`filter-${key}`}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all duration-300 hover:scale-[1.02] ${
                  activeCategory === key
                    ? "bg-brand-blue text-white border-brand-blue shadow-md shadow-brand-blue/25"
                    : "bg-white text-slate-600 border-slate-200 hover:border-brand-blue hover:text-brand-blue"
                }`}
              >
                {t.products.categories[key]}
              </button>
            ))}
          </div>
        </div>

        <div ref={contentRef} className={`reveal-scale ${contentVisible ? 'visible' : ''}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-brand-blue flex items-center justify-center">
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-brand-blue text-sm">{t.products.categories[activeCategory]}</h3>
              <p className="text-slate-400 text-[11px]">{activeProducts.length} {lang === "en" ? "products" : "个产品"}</p>
            </div>
            <div className="flex-1 h-px bg-slate-100 ml-3" />
          </div>

          <div className={`grid gap-3 ${isSingleProductCategory ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
            {activeProducts.map((product, i) => (
              <Card
                key={`${activeCategory}-${i}`}
                data-testid={`card-${activeCategory}-${i}`}
                className="bg-card-mint border border-brand-blue/20 hover-lift hover-glow group cursor-default"
              >
                <CardHeader className="pb-2 pt-5 px-5">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base font-semibold text-[#333333] group-hover:text-brand-blue transition-colors duration-300 leading-snug">
                      {lang === "en" ? product.name : product.nameZh}
                    </CardTitle>
                    <div className="w-8 h-8 rounded-lg bg-brand-blue flex items-center justify-center flex-shrink-0 ml-2 transition-colors duration-300">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5 pt-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-label-muted uppercase tracking-wide">{t.products.specification}</span>
                    <span className="text-xs font-semibold text-brand-blue bg-white/60 px-2.5 py-0.5 rounded-md font-mono">
                      {product.specs}
                    </span>
                  </div>
                  {product.details && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setSelectedProduct(product)}
                        className="inline-flex items-center rounded-md border border-brand-blue/30 bg-white/70 px-3 py-1.5 text-sm font-bold text-brand-blue transition-colors hover:bg-white hover:border-brand-blue"
                      >
                        {isEn ? "View full details" : "查看完整详情"}
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white border-brand-blue/20">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-brand-blue text-xl pr-8">
                  {isEn ? selectedProduct.name : selectedProduct.nameZh}
                </DialogTitle>
                <DialogDescription className="text-xs font-semibold text-brand-blue bg-card-mint px-2.5 py-1 rounded-md font-mono w-fit">
                  {selectedProduct.specs}
                </DialogDescription>
              </DialogHeader>
              <ProductDetailsContent details={selectedProduct.details} isEn={isEn} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
